import { z } from 'zod';
import { listingValues, numberParameter, switchParameter, unchangedFrom, overrideZoom, overrideZoomParameter } from './shared.js';

const radialOptions = {
  rays_across_diameter: z.number().int(),
  scan_x: z.enum(listingValues), scan_y: z.enum(listingValues),
  list_centers: z.enum(listingValues), plot: z.enum(listingValues),
  // Viewer support, not native domain bounds: zero/negative native scale
  // commands return listings, but their graphical behavior remains unverified.
  maximum_diameter: z.number().positive(), diameter_increment: z.number().positive(),
};
const radialRowValues = {
  zoom: overrideZoom,
  rays_across_diameter: radialOptions.rays_across_diameter.nullable().optional(),
  scan_x: radialOptions.scan_x.optional(), scan_y: radialOptions.scan_y.optional(),
  list_centers: radialOptions.list_centers.optional(), plot: radialOptions.plot.optional(),
  maximum_diameter: radialOptions.maximum_diameter.nullable().optional(),
  diameter_increment: radialOptions.diameter_increment.nullable().optional(),
};
// An unfinished row must not dispatch a licensed job, so each member requires
// one option. A union of strict objects, not an intersection with a loose
// union: Zod's intersection keeps a key only the loose side admits (#7563).
// The generated JSON Schema carries the same rule to Python.
const radialRow = z.union(Object.entries(radialOptions).map(([key, schema]) => z.strictObject({ ...radialRowValues, [key]: schema })));
const radialSettings = z.strictObject({
  option_settings: z.array(radialRow).default([]).describe('Ordered native overrides. NRD defaults to 20; omitted XSC/YSC let CODE V choose scans from symmetry; COO defaults No, PLO Yes. XMA/XDE set the plotted diameter maximum/tick increment in native output units. Omitted cells do not reset earlier overrides; no axis default is calculated.'),
  percentages: z.array(z.strictObject({ zoom: overrideZoom, percentage: z.number().int() })).max(10).default([])
    .describe('One native PER command per row, in input order; at most ten commands total. Each command adds a slot at every position: unselected positions retain the native default for that slot. Empty uses native 10,20,...,100. Native output may sort and retain duplicate percentages. Dense different percentages in the same slot are not supported.'),
});
export default {
  label: 'Radial Energy', group: 'performance',
  description: 'Geometrical encircled energy, with the diameters that enclose each requested percentage and the circle centers.',
  settingsSchema: radialSettings,
  parameters: [
    { key: 'option_settings', label: 'Options', kind: 'rows', columns: [overrideZoomParameter,
      // NRD 20, COO No and PLO Yes: blank matched them byte-identically on four lenses (coordinator probe, 2026-10-23).
      { ...numberParameter('rays_across_diameter', 'Rays across diameter', { integer: true, blank: 'Unchanged' }), omit: unchangedFrom('20') },
      { ...switchParameter('scan_x', 'Scan X'), omit: { first: 'Default (symmetry)', later: 'Unchanged' } },
      { ...switchParameter('scan_y', 'Scan Y'), omit: { first: 'Default (symmetry)', later: 'Unchanged' } },
      { ...switchParameter('list_centers', 'List centers'), omit: unchangedFrom('No') }, { ...switchParameter('plot', 'Plot'), omit: unchangedFrom('Yes') },
      { ...numberParameter('maximum_diameter', 'Maximum diameter', { blank: 'Unchanged' }), omit: { first: 'Default', later: 'Unchanged' } },
      { ...numberParameter('diameter_increment', 'Diameter tick increment', { blank: 'Unchanged' }), omit: { first: 'Default', later: 'Unchanged' } },
    ] },
    { key: 'percentages', label: 'Percentages', kind: 'rows', columns: [overrideZoomParameter,
      numberParameter('percentage', 'Energy (%)', { integer: true }),
    ] },
  ],
  coverage: 'Geometrical energy only, for all fields and focus positions; through-focus settings come from the lens. Axis limits must be positive.',
} as const;
